import styled from '@emotion/styled';
import { Button, theme } from '@travelnest/hui';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { FormInput } from '../../../components/form';
import StyledHeading from '../../../components/typography/StyledHeading';
import StyledText from '../../../components/typography/StyledText';

const Container = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	border: 1px solid ${theme.colors.interface.border};
	border-radius: 16px;
	overflow: hidden;

	@media only screen and (min-width: 768px) {
		flex-direction: row;
	}
`;

const ChannelImageBackground = styled.div<{ color: string; image: string; }>`
	width: 100%;
	background-color: ${({ color }) => color};
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 120px;
	background: ${({ image }) => `url(${image})`};
	background-position: center;
    background-repeat: no-repeat;
    background-size: cover;

	@media only screen and (min-width: 768px) {
		max-width: 220px;
	}
`;

const ContentWrapper = styled.div`
	padding: 24px;
	display: flex;
	width: 100%;
	flex-direction: column;

	@media only screen and (min-width: 768px) {
		flex-direction: row;
	}
`;

const Content = styled.div`
	flex-grow: 1;
`;

const TitleWrapper = styled.div`
	margin: 0 0 8px 0;
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
	justify-content: space-between;

	@media only screen and (min-width: 768px) {
		justify-content: unset;
	}
`;
const Title = styled(StyledHeading)`
	color: ${theme.colors.typography.heading};
	margin: unset;
`;

const Text = styled(StyledText)`
	color: ${theme.colors.typography.body};
	margin: 0;
`;

/**
 * TODO: Once we redo the marketing page this component should use Formik and the proper HUI checkbox
 */
const ActionsContainer = styled.div`
	display: flex;
	align-items: baseline;
	margin: 10px 0 0 0;
	flex-direction: row-reverse;
	justify-content: space-between;

	button {
		white-space: nowrap;
	}
	input {
		width: 24px;
		height: 24px;
		border-radius: 8px;

		&:checked {
			background-color: ${theme.colors.BB100};
		}
		&::after {
			top: 2px !important;
			left: 2px !important;
			font-size: 1.25rem !important;
		}
	}
	@media only screen and (min-width: 768px) {
		gap: 24px;
		flex-direction: row;
		justify-content: unset;
		margin: 0 0 0 10px;
	}
`;

export interface IMarketingListItem {
	title: string;
	content: string;
	image: string;
	buttonText: string;
	buttonUrl: string;
	selected: boolean;
	readOnly?: boolean;
	bgColor?: string;
	headingBadge?: JSX.Element | null;
	children?: JSX.Element | null;
	onItemSelect(): void;
}

export const MarketingListItem: React.FC<IMarketingListItem> = ({
	content,
	bgColor = 'rgba(0, 0, 0, 0)',
	image,
	title,
	buttonText,
	buttonUrl,
	selected,
	onItemSelect,
	headingBadge,
	readOnly,
	children
}) => {
	const navigate = useNavigate();
	return (
		<Container>
			<ChannelImageBackground color={bgColor} image={image}>
				{/* <img src={image} /> */}
			</ChannelImageBackground>
			<ContentWrapper>
				<Content>
					<TitleWrapper>
						<Title type="RWB120" lineHeight="md">{title}</Title>
						{headingBadge || null}
					</TitleWrapper>
					<Text type="IR100" lineHeight="md">{content}</Text>
					{children ? <div style={{ marginTop: '8px' }}>{children}</div> : null}
				</Content>
				<ActionsContainer>
					<Button fill="light" variant="secondary" text={buttonText} onClick={() => navigate(buttonUrl)} />
					{!readOnly &&
						<FormInput
							data-testid={`form-input-checkbox-${title}`}
							type="checkbox"
							checked={selected}
							value="standard" onChange={onItemSelect}
						/>
					}
				</ActionsContainer>
			</ContentWrapper>
		</Container>
	);
};
